import { useEffect } from 'react';
import clsx from 'clsx';

import { Size } from '@/utils/typography';
import { Button } from '@/isomorphic/components/typography/button/button';
import { useMyEconomyDrawer } from '@/hooks/use-my-economy-drawer';
import { useContactMeForm } from '@/hooks/use-contact-me-form';
import { sendAsGoal } from '@/hooks/use-ab-tests';
import { useGlobalQueries } from '@/hooks/use-global-queries';
import { GenericEvent, useDataLayer } from '@/hooks/use-data-layer';
import { addScriptTag, deactivateWidget } from '../../page/components/humany-chatbot';

export type Variant = 'standard' | 'arrow' | 'bankidwhite' | 'bankidnorwaywhite' | 'bankidnorwaypurple';

export type ButtonProps = {
    url: string;
    text: TrustedHTML;
    type: 'primary' | 'secondary' | 'secondaryBorder' | 'tertiary' | 'pink' | 'tertiaryNew' | 'transparent' | 'sand' | 'custom';
    size: Size;
    isMyEconomyButton?: boolean;
    isHumanyChatBotButton?: boolean;
    isContactMeButton?: boolean;
    humanyChatBotName?: string;
    myEconomyCourse?: string;
    margin: 'none' | 'top' | 'bottom' | 'topAndBottom';
    variant: Variant;
    fullWidth: boolean;
    abFirstGoal?: {
        useAsGoal: boolean;
        abTestUuid: string;
    };
    gtm?: {
        event: string;
        Product?: string;
        ProductVariant?: string;
    };
    gtmUseCustomProduct?: boolean;
    customBackgroundColor?: string;
    customTextColor?: string;
    customBorderColor?: string;
    customHasBorder?: boolean;
    customHoverBackgroundColor?: string;
    customHoverTextColor?: string;
    customHoverBorderColor?: string;
};

export const ResursButton = ({
    gtm,
    abFirstGoal,
    url = '',
    text = '',
    margin = 'none',
    myEconomyCourse = '',
    isMyEconomyButton = false,
    isContactMeButton = false,
    gtmUseCustomProduct = false,
    isHumanyChatBotButton = false,
    humanyChatBotName = 'resurs-bot',
    ...props
}: ButtonProps) => {
    const { dataLayerPush } = useDataLayer();
    const { getSetting } = useGlobalQueries();
    const { setContactMeForm } = useContactMeForm();
    const { setMyEconomyDrawer } = useMyEconomyDrawer();

    const zendeskSettings = getSetting('zendesk_settings');

    const classNames = clsx({
        'r-mb-6': margin === 'bottom',
        'r-mt-6': margin === 'top',
        'r-my-6': margin === 'topAndBottom',
    });

    useEffect(() => {
        if (isHumanyChatBotButton && !zendeskSettings?.zendesk_chatbot_activate) {
            addScriptTag({ widgetName: humanyChatBotName, activate: false });
        }

        return () => {
            if (isHumanyChatBotButton && !zendeskSettings?.zendesk_chatbot_activate) {
                deactivateWidget(humanyChatBotName);
            }
        };
    }, [isHumanyChatBotButton, zendeskSettings]);

    const sendGtmEvent = () => {
        if (!gtm?.event) {
            return;
        }

        if (gtmUseCustomProduct && gtm?.Product) {
            dataLayerPush({ ...gtm });
            return;
        }

        const event: GenericEvent = {
            event: gtm.event,
        };

        dataLayerPush(event, { usePageProductData: true });
    };

    const onClick = () => {
        sendGtmEvent();

        if (abFirstGoal?.useAsGoal) {
            sendAsGoal(abFirstGoal.abTestUuid);
        }

        if (isMyEconomyButton) {
            setMyEconomyDrawer({ isOpen: true, startCourse: myEconomyCourse });
        }

        if (isHumanyChatBotButton) {
            openChatBot();
        }

        if (isContactMeButton) {
            setContactMeForm({ isOpen: true, showContactBanner: true });
        }
    };

    const openChatBot = async () => {
        if (zendeskSettings?.zendesk_chatbot_activate && zendeskSettings?.zendesk_chatbot_apikey) {
            window?.zE?.('messenger', 'open');
            return;
        }

        try {
            const widget = window.humany?.widgets?.find(humanyChatBotName);
            if (!widget) {
                return;
            }

            if (widget.state !== 'activated') {
                await widget?.invoke('attach');
            }

            await widget.invoke('open');
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <Button className={classNames} to={url?.replaceAll('&amp;', '&')} onClick={onClick} {...props}>
            <span dangerouslySetInnerHTML={{ __html: text }} />
        </Button>
    );
};
